import { useContext, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { resetOrderAction } from '@kfc-global/react-shared/redux/Actions/orderAction';
import { ChannelManager } from 'common/manager';
import { UserOrderContext } from 'context/context';
import { isEmptyEvery, isMobileDevice, redirectToPaymentFailurePage } from 'common/utilities/utils';
import {
  LOCAL_STORAGE_KEYS,
  RESIZE,
  ROUTE_URL,
  SESSION_STORAGE_KEYS,
  ORDER_STATUS,
  PAYMENT_STATUS,
} from 'common/constants/SharedConstants';

import {
  orderDetailsDefaultValue,
  updateOrderDetailsFromStoreReducer,
  updateOrderDetailsFromTenantReducer,
  updateOrderDetailsFromAppStateReducer,
  updateOrderDetailsFromProfileReducer,
  isOrderSummarySaveYourOrderCompletedCheck,
  updateOrderDetailsFromHelpReducer,
  checkCreateOrLoginAccount,
} from 'common/utilities/orderDetailsUtils';
import {
  checkOrderProcessdetails,
  dispatchGetBulkStoreStatusAction,
  orderStatusChangeHandler,
  setLocalStorageDispositionNew,
  triggerGetOrderDetailsAPI,
  updateSetOrderDetailsFromOrderTracking,
  dispatchPostAddHopePayment,
  dispatchPostGiftCardPayment,
  updateStoreTimings,
} from 'common/utilities/orderProcessingSummaryUtils';
import { isAddHopePurchase, isValidOrderStatus, isGiftCardPurchase } from 'common/utilities/orderStatusUtils';
import { getOrderSummaryContent } from 'common/utilities/content';
import { contentfulActions } from '@kfc-global/react-shared/redux/Slices/ContentfulSlice';
import isEmpty from 'lodash/isEmpty';

const OrderProcessingSummaryHOC = props => {
  const { orderDetails, updateSetOrderDetails, children } = props;
  const dispatch = useDispatch();
  const { userOrderState, userOrderStateDispatch } = useContext(UserOrderContext);
  const {
    tenantReducer,
    appStateReducer,
    orderReducer,
    postOrderReducer,
    cartReducer,
    storesReducer,
    paymentsContentReducer,
    helpReducer,
    profileReducer,
    paymentsReducer,
  } = useSelector(data => data);

  const dispositionPaymentPage = isEmptyEvery(userOrderState.dispositionNew)
    ? JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.DISPOSITION_NEW))
    : userOrderState.dispositionNew;

  const resizeHandler = () => {
    updateSetOrderDetails({ isMobileView: isMobileDevice(window.innerWidth) });
  };

  const isOrderSummaryPage = window?.location?.pathname === ROUTE_URL?.ORDER_SUMMARY;

  /*********************************************************************
   * useEffect
   *
   * console.debug('useEffect tenantReducer => ', tenantReducer?.tenantId);
   *********************************************************************/
  useEffect(() => {
    !isEmpty(orderDetails?.service) && dispatch(getOrderSummaryContent(orderDetails?.service));
    return () => {
      dispatch(contentfulActions.resetReducer());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderDetails?.service]);

  /*********************************************************************
   * useEffect
   *
   * console.debug('useEffect tenantReducer => ', tenantReducer?.tenantId);
   *********************************************************************/
  useEffect(() => {
    window.addEventListener(RESIZE, resizeHandler);
    isAddHopePurchase() && dispatch(resetOrderAction());
    isGiftCardPurchase() && dispatch(resetOrderAction());
    return () => {
      window.removeEventListener(RESIZE, resizeHandler);
      updateSetOrderDetails(orderDetailsDefaultValue);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /*********************************************************************
   * useEffect
   * dispatch,  tenantReducer?.tenantId
   * console.debug('useEffect tenantReducer => ', tenantReducer?.tenantId);
   *********************************************************************/
  useEffect(() => {
    if (dispatch && tenantReducer?.tenantId) {
      updateOrderDetailsFromTenantReducer({
        props,
        dispatch,
        tenantReducer,
        dispositionPaymentPage,
        updateSetOrderDetails,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, tenantReducer?.tenantId]);

  /*********************************************************************
   * useEffect
   *
   * console.debug('useEffect tenantReducer => ', tenantReducer?.tenantId);
   *********************************************************************/
  useEffect(() => {
    updateOrderDetailsFromHelpReducer({
      helpReducer,
      updateSetOrderDetails,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [helpReducer]);

  /*********************************************************************
   *  useEffect
   *  console.debug('useEffect [] => ', process?.env);
   *********************************************************************/
  useEffect(() => {
    const miscPaymentReferenceId = paymentsReducer?.addHopePayResponse?.reference;
    orderDetails?.tenantId &&
      isAddHopePurchase() &&
      miscPaymentReferenceId &&
      miscPaymentReferenceId !== orderDetails?.miscPaymentReferenceId &&
      updateSetOrderDetails({ miscPaymentReferenceId, status: ORDER_STATUS?.MISC_PAYMENT_INPROGRESS });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentsReducer?.addHopePayResponse?.reference, orderDetails?.tenantId]);

  /*********************************************************************
   *  useEffect
   *  console.debug('useEffect [] => ', process?.env);
   *********************************************************************/
  useEffect(() => {
    const wiCodePurchaseId = paymentsReducer?.giftCardPayResponse?.reference;
    orderDetails?.tenantId &&
      isGiftCardPurchase() &&
      wiCodePurchaseId &&
      wiCodePurchaseId !== orderDetails?.wiCodePurchaseId &&
      updateSetOrderDetails({ wiCodePurchaseId, status: ORDER_STATUS?.MISC_PAYMENT_INPROGRESS });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentsReducer?.giftCardPayResponse?.reference, orderDetails?.tenantId]);

  /*********************************************************************
   *  useEffect
   *  console.debug('useEffect [] => ', process?.env);
   *********************************************************************/
  useEffect(() => {
    isGiftCardPurchase() &&
      dispatch &&
      orderDetails?.tenantId &&
      orderDetails?.wiCodePurchaseId &&
      dispatchPostGiftCardPayment({ dispatch, orderDetails, updateSetOrderDetails });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, orderDetails?.tenantId, orderDetails?.wiCodePurchaseId]);

  /*********************************************************************
   *  useEffect
   *  console.debug('useEffect [] => ', process?.env);
   *********************************************************************/
  useEffect(() => {
    isAddHopePurchase() &&
      dispatch &&
      orderDetails?.tenantId &&
      orderDetails?.miscPaymentReferenceId &&
      dispatchPostAddHopePayment({ dispatch, orderDetails, updateSetOrderDetails });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, orderDetails?.tenantId, orderDetails?.miscPaymentReferenceId]);

  /*********************************************************************
   * useEffect
   * appStateReducer?.userStatus
   * console.debug('useEffect appStateReducer => ',appStateReducer);
   *********************************************************************/
  useEffect(() => {
    if (appStateReducer?.userStatus) {
      updateOrderDetailsFromAppStateReducer({
        appStateReducer,
        updateSetOrderDetails,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appStateReducer?.userStatus]);

  /*********************************************************************
   * useEffect
   * profileReducer?.favouriteOrders, profileReducer?.savedAddresses
   * console.debug('useEffect profileReducer => ',profileReducer);
   *********************************************************************/
  useEffect(() => {
    if (profileReducer?.favouriteOrders || profileReducer?.savedAddresses) {
      updateOrderDetailsFromProfileReducer({
        profileReducer,
        orderDetails,
        updateSetOrderDetails,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileReducer?.favouriteOrders, profileReducer?.savedAddresses]);

  /*********************************************************************
   * useEffect
   * profileReducer?.favouriteOrders, profileReducer?.savedAddresses
   * console.debug('useEffect profileReducer => ',profileReducer);
   *********************************************************************/
  useEffect(() => {
    const isCreateOrLoginKFCAccount = sessionStorage.getItem(SESSION_STORAGE_KEYS.ORDER_TRACKING_CLICK_KFC_ACCOUNT);
    if (appStateReducer?.userStatus?.loggedIn && isCreateOrLoginKFCAccount !== profileReducer?.basic?.id) {
      sessionStorage.removeItem(SESSION_STORAGE_KEYS.ORDER_TRACKING_CLICK_KFC_ACCOUNT);
      updateSetOrderDetails({ customerId: profileReducer?.basic?.id, customer: profileReducer?.basic });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appStateReducer?.userStatus?.loggedIn, profileReducer?.basic?.id]);

  /*********************************************************************
   * useEffect
   *  orderDetails?.tenantId
   * console.debug('useEffect orderDetails?.tenantId => ', orderDetails);
   *********************************************************************/
  useEffect(() => {
    const isCreateOrLoginKFCAccount = sessionStorage.getItem(SESSION_STORAGE_KEYS.ORDER_TRACKING_CLICK_KFC_ACCOUNT);
    if (orderDetails?.tenantId && !isCreateOrLoginKFCAccount && !isAddHopePurchase() && !isGiftCardPurchase()) {
      updateSetOrderDetailsFromOrderTracking({
        dispatch,
        postOrderReducer,
        orderReducer,
        orderDetails,
        updateSetOrderDetails,
      });

      (async () => {
        await ChannelManager.BindOrder();
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderDetails?.tenantId]);

  /*********************************************************************
   * useEffect
   * orderReducer?.response?.order
   * console.debug('useEffect orderReducer?.response?.order => ', orderReducer?.response?.order?.id);
   *********************************************************************/
  useEffect(() => {
    checkOrderProcessdetails({ orderReducer, paymentsContentReducer, orderDetails, updateSetOrderDetails, props });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderReducer?.response?.order]);

  /*********************************************************************
   * useEffect
   * storesReducer
   * console.debug('useEffect storesReducer => ');
   *********************************************************************/
  useEffect(() => {
    if (storesReducer) {
      updateOrderDetailsFromStoreReducer({ storesReducer, updateSetOrderDetails });
    }
    updateStoreTimings(storesReducer, orderDetails, updateSetOrderDetails);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storesReducer]);

  /*********************************************************************
   * useEffect
   * postOrderReducer?.orderAccepted, orderReducer?.pastOrderResponse
   * console.debug('useEffect postOrderReducer orderReducer => ', isPostOrPastOrderDetailsAvailable({ orderReducer, postOrderReducer }),);
   * console.debug('useEffect postOrderReducer orderReducer paymentsContentReducer => ', paymentsContentReducer);
   *********************************************************************/
  useEffect(() => {
    checkCreateOrLoginAccount({
      orderDetails,
      orderReducer,
      postOrderReducer,
      paymentsContentReducer,
      updateSetOrderDetails,
      dispatch,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    orderDetails?.tenantId,
    postOrderReducer?.orderAccepted,
    postOrderReducer?.addHopeOrderResponse,
    postOrderReducer?.giftCardPurchaseResponse,
    orderReducer?.pastOrderResponse,
  ]);

  /*********************************************************************
   * useEffect
   * orderDetails?.orderId
   * console.debug('useEffect orderDetails?.orderId => ', orderDetails?.orderId);
   *********************************************************************/
  useEffect(() => {
    const isCreateOrLoginKFCAccount = sessionStorage.getItem(SESSION_STORAGE_KEYS.ORDER_TRACKING_CLICK_KFC_ACCOUNT);
    if (orderDetails?.orderId && !isCreateOrLoginKFCAccount) {
      triggerGetOrderDetailsAPI({
        dispatch,
        postOrderReducer,
        orderReducer,
        orderDetails,
        updateSetOrderDetails,
      });

      if (!orderDetails?.isGetStoreStatus && isOrderSummaryPage) {
        dispatchGetBulkStoreStatusAction({ orderDetails, dispatch });
        setLocalStorageDispositionNew({ orderDetails, dispositionPaymentPage, userOrderStateDispatch });
        userOrderStateDispatch({
          isGetStoreStatus: true,
        });
        updateSetOrderDetails({
          orderSummarySaveYourOrderCompleted: isOrderSummarySaveYourOrderCompletedCheck(orderDetails),
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderDetails?.orderId, orderDetails?.miscPaymentReferenceId, orderDetails?.wiCodePurchaseId]);

  /*********************************************************************
   * useEffect
   * orderDetails?.orderStatus
   * console.debug('useEffect orderDetails?.orderStatus => ', orderDetails?.orderStatus);
   *********************************************************************/
  useEffect(() => {
    (orderDetails?.orderId || orderDetails?.miscPaymentReferenceId || orderDetails?.wiCodePurchaseId) &&
      orderDetails?.orderStatus &&
      orderStatusChangeHandler({
        dispatch,
        orderDetails,
        cartReducer,
        userOrderState,
        appStateReducer,
        updateSetOrderDetails,
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderDetails?.orderStatus]);

  /*********************************************************************
   * useEffect
   * orderDetails?.retryCountForGetOrderStatus
   * console.debug('useEffect orderDetails?.retryCountForGetOrderStatus => ', orderDetails?.retryCountForGetOrderStatus);
   *********************************************************************/
  useEffect(() => {
    orderDetails?.tenantId &&
      isValidOrderStatus(orderDetails) &&
      orderDetails?.displayOrderProcessingPage &&
      orderDetails?.retryCountForGetOrderStatus > 0 &&
      triggerGetOrderDetailsAPI({
        dispatch,
        postOrderReducer,
        orderReducer,
        orderDetails,
        updateSetOrderDetails,
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderDetails?.retryCountForGetOrderStatus]);

  useEffect(() => {
    if (postOrderReducer?.orderAccepted?.isWicodeFinaliseFailed) {
      localStorage.setItem(PAYMENT_STATUS.STATUS_TEXT, PAYMENT_STATUS.FAILED);
      redirectToPaymentFailurePage(props);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postOrderReducer?.orderAccepted?.isWicodeFinaliseFailed]);

  /*********************************************************************
   * useEffect
   *
   * console.debug('useEffect tenantReducer => ', tenantReducer?.tenantId);
   *********************************************************************/
  useEffect(() => {
    orderDetails?.orderId &&
      ((postOrderReducer?.orderAccepted?.id && orderDetails?.orderId !== postOrderReducer?.orderAccepted?.id) ||
        (orderReducer?.pastOrderResponse?.[0]?.id &&
          orderDetails?.orderId !== orderReducer?.pastOrderResponse?.[0]?.id) ||
        (orderReducer?.response?.order?.id && orderDetails?.orderId !== orderReducer?.response?.order?.id)) &&
      orderDetails?.dispatchDeliveryOrderTrackerTimer &&
      clearTimeout(orderDetails?.dispatchDeliveryOrderTrackerTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    orderDetails?.dispatchDeliveryOrderTrackerTimer,
    orderDetails?.orderId,
    postOrderReducer?.orderAccepted?.id,
    orderReducer?.pastOrderResponse,
    orderReducer?.response?.order?.id,
  ]);

  return children;
};
export default OrderProcessingSummaryHOC;
